import React, { useState, useEffect } from 'react';
import IconChevron from './svg/IconChevron';
import PropTypes from 'prop-types';
import Tooltip from './Tooltip';

const AsSelect = ({
    id = 'asSelect',
    name,
    title,
    displayFieldName,
    initialValue,
    value,
    values,
    onChange,
    disabled = false,
    labelStyle,
    inputStyle,
    selectStyle,
    optionStyle,
    disabledStyle,
    chevronWidth = 11,
    hideChevron = false,
    chevronProps,
    placeholder,
    containerStyle,
    tooltipStyle,
    disableTooltip = false,
    ...props
}) => {
    const [toolTipText, setToolTipText] = useState('');

    const handleChange = (event) => {
        if (disabled) return;
        onChange(event);
    };

    labelStyle = {
        display: 'block',
        textAlign: 'left',
        ...props.style,
        ...labelStyle
    };

    disabledStyle = {
        backgroundColor: '#F7F7F7',
        cursor: 'default',
        opacity: 1,
        ...disabledStyle
    };

    selectStyle = {
        boxSizing: 'border-box',
        width: '50%',
        padding: 12,
        border: '1px solid #D7D7D7',
        borderRadius: 5,
        resize: 'vertical',
        display: 'block',
        textAlign: 'left',
        backgroundColor: '#FFFDF4',
        color: '#414141',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        appearance: 'none',
        cursor: 'pointer',
        ...(disabled ? disabledStyle : {}),
        ...props.style,
        // Select style is here for backwards compatibility
        ...(inputStyle ?? selectStyle)
    };

    useEffect(() => {
        if (value) {
            setToolTipText(values.find((s) => s.id === value)?.name);
        } else {
            if (values?.length) setToolTipText(values[0].name);
        }
    }, [value, values]);

    return (
        <div id={`${id}Container`} style={containerStyle}>
            <label id={`${id}Label`} style={labelStyle} htmlFor={name}>
                {title}
            </label>

            <Tooltip
                text={toolTipText}
                id={`${id}Tooltip`}
                disabled={disableTooltip}
                containerStyle={{
                    boxSizing: 'border-box',
                    width: '100%',
                    ...tooltipStyle
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    <select
                        {...props}
                        id={id}
                        disabled={disabled}
                        name={name}
                        onChange={handleChange}
                        defaultValue={initialValue}
                        value={value === undefined ? '' : value}
                        style={selectStyle}
                    >
                        {placeholder && (
                            <option
                                style={optionStyle}
                                value=''
                                disabled
                                hidden
                            >
                                {placeholder}
                            </option>
                        )}
                        {values.map((field, i) => (
                            <React.Fragment key={i}>
                                <option
                                    style={optionStyle}
                                    value={field.id.toString()}
                                >
                                    {field[displayFieldName]
                                        ? field[displayFieldName]
                                        : field.desc
                                        ? field.desc
                                        : field.name}
                                </option>
                            </React.Fragment>
                        ))}
                    </select>

                    <IconChevron
                        {...chevronProps}
                        id={`${id}Chevron`}
                        size={chevronWidth}
                        style={{
                            boxSizing: 'border-box',
                            position: 'relative',
                            marginRight: -chevronWidth,
                            right: chevronWidth + 12,
                            pointerEvents: 'none',
                            opacity: 1 * !hideChevron,
                            cursor: disabled ? 'default' : 'pointer',
                            backgroundColor: disabled ? '#F7F7F7' : '#FFFDF4',
                            ...chevronProps?.style
                        }}
                        fill={
                            chevronProps?.fill ||
                            (disabled ? '#414141' : '#000000')
                        }
                    />
                </div>
            </Tooltip>
        </div>
    );
};

AsSelect.defaultProps = {
    id: 'asSelect',
    disabled: false,
    chevronWidth: 11,
    hideChevron: false
};

AsSelect.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    initialValue: PropTypes.any,
    value: PropTypes.any,
    values: PropTypes.array,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    labelStyle: PropTypes.object,
    inputStyle: PropTypes.object,
    optionStyle: PropTypes.object,
    tooltipStyle: PropTypes.object,
    chevronWidth: PropTypes.number,
    hideChevron: PropTypes.bool,
    chevronProps: PropTypes.object,
    placeholder: PropTypes.string
};

export default AsSelect;
